import {
  Container,
  Title,
  Paper,
  Text,
  Badge,
  SimpleGrid,
  Card,
  Loader,
  Alert,
  Group,
  Stack,
  ThemeIcon,
  Button,
  Progress,
  Divider,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconTrendingUp,
  IconStar,
  IconTruck,
  IconHeadset,
  IconReceipt,
  IconPackage,
  IconThumbUp,
  IconArrowRight,
  IconSparkles,
  IconChecklist,
} from '@tabler/icons-react';
import { useCustomerFeedbackInsights } from '@/hooks/usePortalApi';

function MetricCard({
  icon,
  label,
  value,
  subtitle,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
}) {
  return (
    <Card shadow="xs" p="md" radius="md" withBorder>
      <Group gap="sm">
        <ThemeIcon size="lg" variant="light" color="blue" radius="md">
          {icon}
        </ThemeIcon>
        <div style={{ flex: 1 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
          <Text size="xl" fw={700} c="blue.7">{value}</Text>
          {subtitle && <Text size="xs" c="dimmed">{subtitle}</Text>}
        </div>
      </Group>
    </Card>
  );
}

const SIGNAL_CONFIG = [
  { key: 'claims', label: 'Quality Reviews', icon: IconChecklist, color: 'blue' },
  { key: 'tickets', label: 'Support Inquiries', icon: IconHeadset, color: 'blue' },
  { key: 'disputes', label: 'Billing Reviews', icon: IconReceipt, color: 'blue' },
  { key: 'returns', label: 'Returns Processed', icon: IconPackage, color: 'cyan' },
  { key: 'delivery_exceptions', label: 'Delivery Adjustments', icon: IconTruck, color: 'cyan' },
  { key: 'feedback', label: 'Feedback Submitted', icon: IconThumbUp, color: 'indigo' },
] as const;

export default function CustomerFeedbackInsights() {
  const { data, isLoading } = useCustomerFeedbackInsights();

  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Stack align="center" gap="md" py="xl">
          <Loader size="lg" color="blue" />
          <Text c="dimmed">Loading your account insights...</Text>
        </Stack>
      </Container>
    );
  }

  const riskLevel = data?.risk_level ?? 'low';
  const signals = data?.signal_summary ?? {};
  const narrative = data?.ai_narrative;
  const healthScore = data?.health_score;
  const actions = data?.recommended_actions ?? [];
  const totalSignals = signals.total ?? 0;
  const healthValue = typeof healthScore?.overall_score === 'number'
    ? healthScore.overall_score
    : 85;

  const allClear = !data || totalSignals === 0;

  return (
    <Container size="lg" py="xl">
      {/* Header */}
      <Group mb="xl">
        <ThemeIcon size={48} variant="light" color="blue" radius="xl">
          <IconShieldCheck size={28} />
        </ThemeIcon>
        <div>
          <Title order={2} c="blue.8">Your Account at a Glance</Title>
          <Text c="dimmed" size="sm">
            Real-time insights into your service quality and account health
          </Text>
        </div>
      </Group>

      {/* Top metrics row */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mb="xl">
        <MetricCard
          icon={<IconTrendingUp size={20} />}
          label="Account Health"
          value={healthValue}
          subtitle="out of 100"
        />
        <MetricCard
          icon={<IconShieldCheck size={20} />}
          label="Status"
          value={riskLevel === 'low' ? 'Excellent' : riskLevel === 'medium' ? 'Good' : 'Needs Attention'}
          subtitle={riskLevel === 'low' ? 'All systems go' : 'We\'re working on it'}
        />
        <MetricCard
          icon={<IconStar size={20} />}
          label="Service Items"
          value={totalSignals}
          subtitle="in the last 90 days"
        />
        <MetricCard
          icon={<IconHeadset size={20} />}
          label="Team Engaged"
          value={data?.active_alerts?.length ?? 0}
          subtitle="active follow-ups"
        />
      </SimpleGrid>

      {/* AI Summary — the hero section */}
      {narrative && (
        <Paper
          p="lg"
          mb="xl"
          radius="md"
          style={{
            background: 'linear-gradient(135deg, var(--mantine-color-blue-0) 0%, var(--mantine-color-indigo-0) 100%)',
            border: '1px solid var(--mantine-color-blue-2)',
          }}
        >
          <Group gap="sm" mb="sm">
            <ThemeIcon size="sm" variant="light" color="blue" radius="xl">
              <IconSparkles size={14} />
            </ThemeIcon>
            <Text fw={600} c="blue.8">AI-Powered Account Summary</Text>
          </Group>
          <Text size="sm" lh={1.6}>{narrative}</Text>
        </Paper>
      )}

      {/* Health progress bar */}
      <Paper p="lg" withBorder radius="md" mb="xl">
        <Group justify="space-between" mb="xs">
          <Text fw={600}>Account Health Score</Text>
          <Badge color="blue" variant="light" size="lg">{healthValue}/100</Badge>
        </Group>
        <Progress
          value={healthValue}
          size="xl"
          radius="xl"
          color={healthValue >= 80 ? 'blue' : healthValue >= 60 ? 'cyan' : 'yellow'}
        />
        <Text size="xs" c="dimmed" mt="xs">
          {healthValue >= 80
            ? 'Your account is in great standing — thank you for being a valued partner!'
            : healthValue >= 60
              ? 'Your account is in good shape. We\'re addressing a few items to make it even better.'
              : 'We\'re actively working to improve your experience. Your rep will be in touch.'}
        </Text>
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="xl">
        {/* Recent Activity */}
        {totalSignals > 0 && (
          <Paper p="lg" withBorder radius="md">
            <Text fw={600} mb="md">Recent Activity</Text>
            <Stack gap="sm">
              {SIGNAL_CONFIG.map(({ key, label, icon: Icon, color }) => {
                const count = signals[key];
                if (!count || count <= 0) return null;
                return (
                  <Group key={key} justify="space-between">
                    <Group gap="sm">
                      <ThemeIcon size="sm" variant="light" color={color} radius="md">
                        <Icon size={14} />
                      </ThemeIcon>
                      <Text size="sm">{label}</Text>
                    </Group>
                    <Badge color={color} variant="light" radius="xl">{count}</Badge>
                  </Group>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* What We're Doing */}
        {actions.length > 0 && (
          <Paper p="lg" withBorder radius="md">
            <Text fw={600} mb="md">What We're Doing For You</Text>
            <Stack gap="sm">
              {actions.map((a: any, i: number) => (
                <Group key={i} gap="sm" align="flex-start">
                  <ThemeIcon size="xs" variant="filled" color="blue" radius="xl" mt={4}>
                    <IconArrowRight size={10} />
                  </ThemeIcon>
                  <Text size="sm" style={{ flex: 1 }}>{a.action}</Text>
                </Group>
              ))}
            </Stack>
          </Paper>
        )}
      </SimpleGrid>

      {/* Active follow-ups banner */}
      {data?.active_alerts?.length > 0 && (
        <Alert
          color="blue"
          variant="light"
          radius="md"
          mb="xl"
          title="Your Dedicated Team Is On It"
          icon={<IconHeadset size={20} />}
        >
          <Text size="sm">
            We have {data.active_alerts.length} active follow-up{data.active_alerts.length > 1 ? 's' : ''} in
            progress to ensure your experience keeps improving. Your sales rep has full
            visibility and will reach out with updates.
          </Text>
        </Alert>
      )}

      {/* All clear state */}
      {allClear && (
        <Paper
          p="xl"
          radius="md"
          ta="center"
          style={{
            background: 'linear-gradient(135deg, var(--mantine-color-blue-0) 0%, var(--mantine-color-cyan-0) 100%)',
            border: '1px solid var(--mantine-color-blue-2)',
          }}
        >
          <ThemeIcon size={56} variant="light" color="blue" radius="xl" mx="auto" mb="md">
            <IconThumbUp size={30} />
          </ThemeIcon>
          <Title order={3} c="blue.8" mb="xs">Everything Looks Great!</Title>
          <Text c="dimmed" size="sm" maw={400} mx="auto">
            No recent issues to report. Your account is running smoothly and your
            orders are being handled with care.
          </Text>
        </Paper>
      )}

      {/* CTA footer */}
      <Divider my="xl" />
      <Paper p="lg" radius="md" bg="blue.0">
        <Group justify="space-between" align="center">
          <div>
            <Text fw={600} c="blue.8">Need something? We're here to help.</Text>
            <Text size="sm" c="dimmed">
              Reach out to your dedicated sales rep or contact our support team anytime.
            </Text>
          </div>
          <Group>
            <Button variant="light" color="blue" size="sm">Contact Support</Button>
            <Button color="blue" size="sm">Talk to Your Rep</Button>
          </Group>
        </Group>
      </Paper>
    </Container>
  );
}
